'use client';

import React, { useState } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { usePortfolioData } from '@/context/PortfolioDataContext';
import CalendlyModal from './CalendlyModal';
import TeklifModal from './TeklifModal';

export default function HeroSection() {
  const { t } = useLanguage();
  const { hero, siteSettings, contact, loading } = usePortfolioData();
  const [calendlyOpen, setCalendlyOpen] = useState(false);
  const [teklifOpen, setTeklifOpen] = useState(false);

  // Wait for data to load before rendering to prevent flash of fallback values
  if (loading) {
    return (
      <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden px-6 pb-20 pt-8">
        <div
          className="absolute inset-0 z-0"
          style={{ background: 'linear-gradient(180deg, #0d1117 0%, #141c28 50%, #1B2432 100%)' }}
        />
        {/* Skeleton content */}
        <div className="relative z-10 flex flex-col items-start text-left max-w-4xl mx-auto w-full animate-pulse">
          {/* Status badge skeleton */}
          <div
            className="h-7 w-40 rounded-full mb-8"
            style={{ background: 'rgba(99,179,237,0.1)' }}
          />
          {/* Greeting skeleton */}
          <div className="h-4 w-24 rounded mb-3" style={{ background: 'rgba(99,179,237,0.08)' }} />
          {/* Name skeleton */}
          <div
            className="h-16 w-80 rounded-lg mb-4"
            style={{ background: 'rgba(237,242,247,0.07)' }}
          />
          {/* Title + skill skeleton */}
          <div className="flex items-center gap-3 mb-6 flex-wrap">
            <div className="h-8 w-56 rounded-lg" style={{ background: 'rgba(99,179,237,0.1)' }} />
            <div className="h-8 w-40 rounded-lg" style={{ background: 'rgba(237,242,247,0.05)' }} />
          </div>
          {/* Bio skeleton — 3 lines */}
          <div className="flex flex-col gap-2 max-w-2xl w-full mb-10">
            <div className="h-4 w-full rounded" style={{ background: 'rgba(237,242,247,0.06)' }} />
            <div className="h-4 w-5/6 rounded" style={{ background: 'rgba(237,242,247,0.06)' }} />
            <div className="h-4 w-3/4 rounded" style={{ background: 'rgba(237,242,247,0.06)' }} />
          </div>
          {/* CTA buttons skeleton */}
          <div className="flex flex-wrap items-center gap-4">
            <div
              className="h-11 w-36 rounded-full"
              style={{ background: 'rgba(99,179,237,0.12)' }}
            />
            <div
              className="h-11 w-32 rounded-full"
              style={{ background: 'rgba(255,255,255,0.04)' }}
            />
            <div className="h-5 w-20 rounded" style={{ background: 'rgba(99,179,237,0.08)' }} />
            <div className="h-5 w-16 rounded" style={{ background: 'rgba(167,139,250,0.08)' }} />
          </div>
          {/* Stats skeleton */}
          <div
            className="mt-16 flex flex-wrap gap-8 pt-8 w-full"
            style={{ borderTop: '1px solid rgba(200,206,214,0.08)' }}
          >
            {[1, 2, 3]?.map((i) => (
              <div key={i} className="flex flex-col gap-1.5">
                <div
                  className="h-7 w-12 rounded"
                  style={{ background: 'rgba(237,242,247,0.07)' }}
                />
                <div
                  className="h-3 w-20 rounded"
                  style={{ background: 'rgba(237,242,247,0.05)' }}
                />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  const name = hero?.name ?? siteSettings?.site_name ?? '';
  const title = hero?.title ?? '';
  const skillName = hero?.skill_name ?? siteSettings?.tech_stack_line ?? '';
  const bio = hero?.bio ?? '';
  const cvUrl = hero?.cv_url ?? contact?.cv_url ?? '';
  const linkedinUrl = hero?.linkedin_url ?? contact?.linkedin_url ?? '';
  const calendlyUrl = siteSettings?.calendly_url ?? '';

  const _stats = [
    { value: '14+', label: t?.hero_stat_years },
    { value: '5+', label: t?.hero_stat_companies },
    { value: t?.hero_stat_stack, label: t?.hero_stat_stack_sub },
  ];

  return (
    <>
      <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden px-6 pb-20 pt-8">
        {/* Deep void gradient */}
        <div
          className="absolute inset-0 z-0"
          style={{
            background: 'linear-gradient(180deg, #0d1117 0%, #141c28 50%, #1B2432 100%)',
          }}
        />
        {/* Atmospheric radial glow */}
        <div
          className="absolute z-0 glow-pulse pointer-events-none"
          style={{
            width: '70vw',
            maxWidth: 700,
            height: '70vw',
            maxHeight: 700,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -54%)',
            background:
              'radial-gradient(ellipse 55% 45% at 50% 50%, rgba(99,179,237,0.18) 0%, rgba(99,179,237,0.05) 55%, transparent 75%)',
            borderRadius: '50%',
            filter: 'blur(2px)',
          }}
        />
        {/* Grid lines background */}
        <div
          className="absolute inset-0 z-0 opacity-[0.04]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(99,179,237,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(99,179,237,0.5) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />
        {/* Content */}
        <div className="relative z-10 flex flex-col items-start text-left max-w-4xl mx-auto w-full">
          {/* Status badge */}
          <div
            className="hero-cta inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-8 text-xs font-semibold uppercase tracking-[0.18em]"
            style={{
              background: 'rgba(99,179,237,0.1)',
              border: '1px solid rgba(99,179,237,0.3)',
              color: '#63b3ed',
            }}
          >
            <span
              className="inline-block w-1.5 h-1.5 rounded-full"
              style={{ background: '#63b3ed', boxShadow: '0 0 6px #63b3ed' }}
            />
            {t?.hero_open}
          </div>

          {/* Name */}
          <div className="hero-title mb-2">
            <span
              className="font-mono text-sm font-medium tracking-widest uppercase"
              style={{ color: 'rgba(99,179,237,0.7)' }}
            >
              {t?.hero_greeting}
            </span>
          </div>
          <h1
            className="hero-title font-display font-bold leading-[1.0] tracking-tight mb-4"
            style={{
              fontSize: 'clamp(3rem, 8vw, 6rem)',
              color: '#EDF2F7',
            }}
          >
            {name}
          </h1>

          {/* Title */}
          <div className="hero-sub mb-6 flex items-center gap-3 flex-wrap">
            <span
              className="font-display font-semibold"
              style={{
                fontSize: 'clamp(1.25rem, 3vw, 2rem)',
                background: 'linear-gradient(135deg, #63b3ed 0%, #a78bfa 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {title}
            </span>
            <span style={{ color: 'rgba(237,242,247,0.2)', fontSize: '1.5rem' }}>·</span>
            <span
              className="font-display font-medium"
              style={{
                fontSize: 'clamp(1rem, 2.5vw, 1.5rem)',
                color: 'rgba(237,242,247,0.5)',
              }}
            >
              {skillName}
            </span>
          </div>

          {/* Bio */}
          <p
            className="hero-cta font-body text-lg leading-relaxed max-w-2xl mb-10"
            style={{ color: 'rgba(237,242,247,0.6)' }}
          >
            {bio}
          </p>

          {/* CTA row */}
          <div className="hero-cta flex flex-wrap items-center gap-4">
            <a
              href="#projects"
              className="cta-pill inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full font-display font-bold text-white text-sm"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <rect
                  x="1.5"
                  y="2.5"
                  width="13"
                  height="11"
                  rx="2"
                  stroke="white"
                  strokeWidth="1.4"
                />
                <path
                  d="M5 6l3 3 3-3"
                  stroke="white"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {t?.hero_cta_projects}
            </a>

            {calendlyUrl ? (
              <button
                onClick={() => setCalendlyOpen(true)}
                className="inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full font-display font-semibold text-sm transition-all duration-300 hover:scale-105"
                style={{
                  background: 'rgba(99,179,237,0.08)',
                  border: '1px solid rgba(99,179,237,0.3)',
                  color: '#63b3ed',
                }}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <rect
                    x="1.5"
                    y="2"
                    width="13"
                    height="12"
                    rx="2"
                    stroke="currentColor"
                    strokeWidth="1.3"
                  />
                  <path
                    d="M5 1v2M11 1v2"
                    stroke="currentColor"
                    strokeWidth="1.3"
                    strokeLinecap="round"
                  />
                  <path d="M1.5 6h13" stroke="currentColor" strokeWidth="1.3" />
                  <rect x="4" y="8.5" width="2" height="2" rx="0.5" fill="currentColor" />
                  <rect x="7" y="8.5" width="2" height="2" rx="0.5" fill="currentColor" />
                  <rect x="10" y="8.5" width="2" height="2" rx="0.5" fill="currentColor" />
                </svg>
                Randevu Al
              </button>
            ) : null}

            {/* Teklif Al */}
            <button
              onClick={() => setTeklifOpen(true)}
              className="inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full font-display font-semibold text-sm transition-all duration-300 hover:scale-105"
              style={{
                background: 'rgba(167,139,250,0.08)',
                border: '1px solid rgba(167,139,250,0.3)',
                color: '#a78bfa',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M3 2h10a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1z"
                  stroke="currentColor"
                  strokeWidth="1.3"
                />
                <path
                  d="M5 5h6M5 7.5h6M5 10h4"
                  stroke="currentColor"
                  strokeWidth="1.3"
                  strokeLinecap="round"
                />
              </svg>
              Teklif Al
            </button>

            <a
              href="#contact"
              className="inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full font-display font-semibold text-sm transition-all duration-300 hover:scale-105"
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(200,206,214,0.18)',
                color: 'rgba(237,242,247,0.75)',
              }}
            >
              {t?.hero_cta_contact}
            </a>
            {linkedinUrl ? (
              <a
                href={linkedinUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm font-medium transition-colors duration-200"
                style={{ color: 'rgba(99,179,237,0.7)' }}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <rect
                    x="1"
                    y="1"
                    width="14"
                    height="14"
                    rx="2.5"
                    stroke="currentColor"
                    strokeWidth="1.4"
                  />
                  <path
                    d="M5 6.5v5M5 4.5v.5"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                  />
                  <path
                    d="M8.5 11.5v-2.5a2 2 0 0 1 4 0v2.5M8.5 6.5v5"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                LinkedIn
              </a>
            ) : null}
            {cvUrl ? (
              <a
                href={cvUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm font-medium transition-colors duration-200"
                style={{ color: 'rgba(167,139,250,0.7)' }}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M4 2h6l4 4v8a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1z"
                    stroke="currentColor"
                    strokeWidth="1.4"
                  />
                  <path
                    d="M9 2v4h4"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                  />
                  <path
                    d="M8 8v4M6 10l2 2 2-2"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {t?.nav_cv}
              </a>
            ) : null}
          </div>

          {/* Stats row */}
          <div
            className="hero-cta mt-16 flex flex-wrap gap-8 pt-8"
            style={{ borderTop: '1px solid rgba(200,206,214,0.08)' }}
          >
            {[
              { value: '14+', label: t?.hero_stat_years },
              { value: '5+', label: t?.hero_stat_companies },
              { value: t?.hero_stat_stack, label: t?.hero_stat_stack_sub },
            ]?.map((stat) => (
              <div key={stat?.label}>
                <p className="font-display font-bold text-2xl" style={{ color: '#EDF2F7' }}>
                  {stat?.value}
                </p>
                <p
                  className="text-xs uppercase tracking-[0.12em] font-medium mt-0.5"
                  style={{ color: 'rgba(237,242,247,0.4)' }}
                >
                  {stat?.label}
                </p>
              </div>
            ))}
          </div>
        </div>
        {/* Bottom chrome divider */}
        <div
          className="absolute bottom-0 left-0 right-0 h-px"
          style={{
            background: 'linear-gradient(90deg, transparent, rgba(200,206,214,0.15), transparent)',
          }}
        />
      </section>

      {/* Modals */}
      {calendlyUrl ? (
        <CalendlyModal
          isOpen={calendlyOpen}
          onClose={() => setCalendlyOpen(false)}
          calendlyUrl={calendlyUrl}
        />
      ) : null}
      <TeklifModal isOpen={teklifOpen} onClose={() => setTeklifOpen(false)} />
    </>
  );
}
